<template>
  <button
    data-test-id="lia-header-button"
    class="lia-header-button"
    :class="{ 'is-expanded': isExpanded }"
    :aria-label="$t('INTEGRATION_SETTINGS.OPEN_AI.LIA.OPEN_SIDEBAR')"
    @click="onClick"
  >
    <img
      class="lia-header-button__avatar"
      src="~dashboard/assets/images/lia-avatar.png"
      :alt="$t('INTEGRATION_SETTINGS.OPEN_AI.LIA.AVATAR_ALT')"
    />
    <span
      class="lia-header-button__message"
      data-test-id="lia-header-message"
      :aria-hidden="!isExpanded"
    >
      {{ nudgeMessage }}
    </span>
  </button>
</template>

<script>
export const NUDGE_DELAY = 90 * 1000;
export const NUDGE_DURATION = 20 * 1000;
export const NUDGE_PHRASES_COUNT = 10;

export default {
  name: 'LiaHeaderButton',
  props: {
    conversationId: {
      type: [Number, String],
      default: null,
    },
    isSidebarOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: false,
      nudgeIndex: null,
      nudgeTimeout: null,
      collapseTimeout: null,
    };
  },
  computed: {
    nudgeMessage() {
      if (this.nudgeIndex === null) return '';
      return this.$t(
        `INTEGRATION_SETTINGS.OPEN_AI.LIA.NUDGES[${this.nudgeIndex}]`
      );
    },
  },
  watch: {
    conversationId(newId, oldId) {
      if (newId === oldId) return;
      this.collapse();
      this.scheduleNudge();
    },
    isSidebarOpen(isOpen) {
      if (isOpen) {
        this.collapse();
        return;
      }
      this.scheduleNudge();
    },
  },
  mounted() {
    this.scheduleNudge();
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    clearTimers() {
      clearTimeout(this.nudgeTimeout);
      clearTimeout(this.collapseTimeout);
      this.nudgeTimeout = null;
      this.collapseTimeout = null;
    },
    collapse() {
      this.clearTimers();
      this.isExpanded = false;
    },
    scheduleNudge() {
      this.clearTimers();
      if (this.isSidebarOpen) return;
      this.nudgeTimeout = setTimeout(this.showNudge, NUDGE_DELAY);
    },
    pickNudgeIndex() {
      const lastIndex = this.nudgeIndex;
      const candidates = [...Array(NUDGE_PHRASES_COUNT).keys()].filter(
        index => index !== lastIndex
      );
      return candidates[Math.floor(Math.random() * candidates.length)];
    },
    showNudge() {
      this.clearTimers();
      this.nudgeIndex = this.pickNudgeIndex();
      this.isExpanded = true;
      this.collapseTimeout = setTimeout(() => {
        this.isExpanded = false;
        this.scheduleNudge();
      }, NUDGE_DURATION);
    },
    onClick() {
      this.collapse();
      this.$emit('open-sidebar');
    },
  },
};
</script>

<style lang="scss" scoped>
.lia-header-button {
  display: inline-flex;
  align-items: center;
  height: 4rem;
  min-width: 4rem;
  padding: 0;
  background: var(--white);
  border: 2px solid #83b8fc;
  border-radius: 10rem;
  color: #0d2693;
  cursor: pointer;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-normal);
  line-height: 1.3;
  text-align: left;
  transition: padding 0.4s ease;

  &.is-expanded {
    padding-right: var(--space-small);

    .lia-header-button__message {
      max-width: 16rem;
      opacity: 1;
      margin-left: var(--space-smaller);
    }
  }
}

.lia-header-button__avatar {
  flex-shrink: 0;
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 50%;
}

.lia-header-button__message {
  display: inline-block;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  width: 16rem;
  white-space: normal;
  transition: max-width 0.4s ease, opacity 0.3s ease, margin 0.4s ease;
}
</style>
